import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, DollarSign, Users, FolderOpen, Trash2, Pencil, Check, X, ArrowLeft, Upload, UserCircle, Archive, ArchiveRestore, Eye, EyeOff, Info } from 'lucide-react';
import { Textarea } from '../components/ui/textarea';
import { formatCurrency } from '../utils/currency';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [projects, setProjects] = useState([]);
  const [allProjects, setAllProjects] = useState([]); // used for book totals (archive-independent)
  const [showArchived, setShowArchived] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [seeded, setSeeded] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);

  // Book breakdown dialog (Contract vs Received)
  const [breakdownBook, setBreakdownBook] = useState(null); // 'freelance' | 'company' | null
  const [bookIncome, setBookIncome] = useState({ freelance: 0, company: 0 });

  // ==== Table customization ====
  const COLUMNS = [
    { key: 'name',         label: 'Project Name',        default: 200 },
    { key: 'client',       label: 'Client',              default: 140 },
    { key: 'book',         label: 'Book',                default: 90  },
    { key: 'contract',     label: 'Contract Value',      default: 130 },
    { key: 'paid',         label: 'Amount Paid',         default: 130 },
    { key: 'teamContract', label: 'Total Team Contract', default: 150 },
    { key: 'teamPaid',     label: 'Paid to Team',        default: 130 },
    { key: 'net',          label: 'Net Profit',          default: 130 },
    { key: 'projNet',      label: 'Projected Net Profit',default: 150 },
    { key: 'milestone',    label: 'Milestone',           default: 110 },
    { key: 'pending',      label: 'Pending Tasks',       default: 110 },
    { key: 'teamSize',     label: 'Team Size',           default: 90  },
    { key: 'actions',      label: 'Actions',             default: 110 },
  ];
  const defaultWidths = COLUMNS.reduce((acc, c) => ({ ...acc, [c.key]: c.default }), {});
  const [colWidths, setColWidths] = useState(() => {
    try { return { ...defaultWidths, ...JSON.parse(localStorage.getItem('cse-col-widths-v1') || '{}') }; } catch { return defaultWidths; }
  });
  const [hiddenCols, setHiddenCols] = useState(() => {
    try { return JSON.parse(localStorage.getItem('cse-hidden-cols-v1') || '[]'); } catch { return []; }
  });
  const [ctxMenu, setCtxMenu] = useState(null); // {x, y, colKey}
  useEffect(() => { localStorage.setItem('cse-col-widths-v1', JSON.stringify(colWidths)); }, [colWidths]);
  useEffect(() => { localStorage.setItem('cse-hidden-cols-v1', JSON.stringify(hiddenCols)); }, [hiddenCols]);
  useEffect(() => {
    const close = () => setCtxMenu(null);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, []);

  const showCol = (k) => !hiddenCols.includes(k);
  const hideCol = (k) => setHiddenCols((prev) => prev.includes(k) ? prev : [...prev, k]);
  const showAllCols = () => setHiddenCols([]);

  // Column resize
  const startResize = (e, key) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = colWidths[key] || 120;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMove = (ev) => {
      const next = Math.max(60, startW + (ev.clientX - startX));
      setColWidths((prev) => ({ ...prev, [key]: next }));
    };
    const onUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // Row drag reorder
  const [dragRowId, setDragRowId] = useState(null);
  const [dragOverRowId, setDragOverRowId] = useState(null);
  const onRowDragStart = (e, id) => { setDragRowId(id); e.dataTransfer.effectAllowed = 'move'; };
  const onRowDragOver = (e, id) => { e.preventDefault(); if (id !== dragOverRowId) setDragOverRowId(id); };
  const onRowDrop = async (e, targetId) => {
    e.preventDefault();
    setDragOverRowId(null);
    if (!dragRowId || dragRowId === targetId) { setDragRowId(null); return; }
    const list = [...projects];
    const fromIdx = list.findIndex(p => p.id === dragRowId);
    const toIdx = list.findIndex(p => p.id === targetId);
    if (fromIdx < 0 || toIdx < 0) { setDragRowId(null); return; }
    const [moved] = list.splice(fromIdx, 1);
    list.splice(toIdx, 0, moved);
    setProjects(list);
    setDragRowId(null);
    try {
      await axios.post(`${API}/projects/reorder`, { orderedIds: list.map(p => p.id) });
    } catch { toast.error('Failed to save order'); fetchData(); }
  };
  
  // Dashboard title editing
  const [dashboardTitle, setDashboardTitle] = useState('Cremona Management Hub');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [tempTitle, setTempTitle] = useState('');
  
  // Project name editing
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [editingProjectName, setEditingProjectName] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    contractValue: '',
    initialPaid: 0,
    teamMembers: [],
    contractFile: null,
    contractFileName: '',
    clientId: '',
    clientName: '',
    subWorks: [],
    isAfterService: false,
    relatedProjectId: '',
    relatedProjectName: '',
    financeBook: 'freelance'
  });
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    fetchData();
  }, [location]); // Re-fetch whenever location changes (including navigation back)

  const fetchData = async (opts = {}) => {
    try {
      const archived = opts.archived !== undefined ? opts.archived : showArchived;
      const [projectsRes, allProjectsRes, teamRes, clientsRes, freelanceSumRes, companySumRes] = await Promise.all([
        axios.get(`${API}/projects?archived=${archived ? 'true' : 'false'}`),
        axios.get(`${API}/projects?archived=all`),
        axios.get(`${API}/team`),
        axios.get(`${API}/clients`),
        axios.get(`${API}/finance/summary?book=freelance`),
        axios.get(`${API}/finance/summary?book=company`)
      ]);
      setProjects(projectsRes.data);
      setAllProjects(allProjectsRes.data);
      setTeamMembers(teamRes.data);
      setClients(clientsRes.data);
      setBookIncome({
        freelance: freelanceSumRes.data?.totalIncome || 0,
        company: companySumRes.data?.totalIncome || 0
      });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
      setLoading(false);
    }
  };

  // Compute whether a project is safely archivable:
  //  (1) all tasks done, (2) client fully paid (initialPaid+amountPaid ≥ contractValue),
  //  (3) each assigned team member fully paid on this project, (4) no pending postponed payment.
  const canArchive = (p) => {
    if ((p.pendingTasks || 0) > 0) return false;
    if ((p.pendingPostponedCount || 0) > 0) return false;
    const clientPaid = (p.initialPaid || 0) + (p.amountPaid || 0);
    if (clientPaid < (p.contractValue || 0)) return false;
    const anyTeamUnpaid = (p.teamMembers || []).some(m =>
      ((m.initialPaid || 0) + (m.amountPaid || 0)) < (m.contractValue || 0)
    );
    if (anyTeamUnpaid) return false;
    return true;
  };

  const toggleArchive = async (e, project) => {
    e.stopPropagation();
    const newArchived = !project.archived;
    if (newArchived && !canArchive(project)) {
      toast.error('Cannot archive yet — outstanding tasks, unpaid client, unpaid team, or pending postponed planner.');
      return;
    }
    try {
      await axios.put(`${API}/projects/${project.id}`, { archived: newArchived });
      toast.success(newArchived ? `Archived "${project.name}"` : `Restored "${project.name}"`);
      fetchData();
    } catch { toast.error('Failed to update project'); }
  };

  const seedData = async () => {
    try {
      await axios.post(`${API}/seed`);
      toast.success('Sample data created!');
      setSeeded(true);
      fetchData();
    } catch (error) {
      console.error('Error seeding data:', error);
      toast.error('Failed to create sample data');
    }
  };

  const addMember = (memberName) => {
    if (selectedMembers.find(m => m.name === memberName)) {
      toast.error('Member already added');
      return;
    }
    
    const member = teamMembers.find(m => m.name === memberName);
    if (member) {
      setSelectedMembers([...selectedMembers, {
        name: member.name,
        role: member.role,
        contractValue: 0,
        initialPaid: 0,
        amountPaid: 0,
        contractDescription: '',
        assignmentDate: '',
        contractFile: null,
        contractFileName: ''
      }]);
    }
  };

  const removeMember = (index) => {
    setSelectedMembers(selectedMembers.filter((_, i) => i !== index));
  };

  const updateMemberValue = (index, field, value) => {
    const updated = [...selectedMembers];
    if (['contractValue', 'initialPaid', 'amountPaid'].includes(field)) {
      updated[index][field] = parseFloat(value) || 0;
    } else {
      updated[index][field] = value;
    }
    setSelectedMembers(updated);
  };

  const handleMemberFileUpload = (index, e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const updated = [...selectedMembers];
      updated[index].contractFile = reader.result;
      updated[index].contractFileName = file.name;
      setSelectedMembers(updated);
    };
    reader.readAsDataURL(file);
  };

  const handleProjectContractUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setFormData({ ...formData, contractFile: reader.result, contractFileName: file.name });
    };
    reader.readAsDataURL(file);
  };

  const addSubWork = () => {
    setFormData({
      ...formData,
      subWorks: [...formData.subWorks, { id: crypto.randomUUID(), name: '', price: 0 }]
    });
  };

  const updateSubWork = (index, field, value) => {
    const updated = [...formData.subWorks];
    updated[index][field] = field === 'price' ? (parseFloat(value) || 0) : value;
    setFormData({ ...formData, subWorks: updated });
  };

  const removeSubWork = (index) => {
    setFormData({ ...formData, subWorks: formData.subWorks.filter((_, i) => i !== index) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.contractValue) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const subWorksTotal = formData.subWorks.reduce((sum, sw) => sum + (sw.price || 0), 0);
      const finalContractValue = formData.subWorks.length > 0 ? (parseFloat(formData.contractValue) || subWorksTotal) : parseFloat(formData.contractValue);
      
      await axios.post(`${API}/projects`, {
        name: formData.name,
        contractValue: finalContractValue,
        initialPaid: formData.isAfterService ? 0 : (parseFloat(formData.initialPaid) || 0),
        teamMembers: selectedMembers,
        subWorks: formData.subWorks,
        clientId: formData.clientId || null,
        clientName: formData.clientName || null,
        contractFile: formData.contractFile,
        contractFileName: formData.contractFileName,
        isAfterService: formData.isAfterService,
        relatedProjectId: formData.relatedProjectId || null,
        relatedProjectName: formData.relatedProjectName || null,
        financeBook: formData.financeBook
      });
      
      toast.success('Project created successfully!');
      setDialogOpen(false);
      setFormData({ name: '', contractValue: '', initialPaid: 0, teamMembers: [], contractFile: null, contractFileName: '', clientId: '', clientName: '', subWorks: [], isAfterService: false, relatedProjectId: '', relatedProjectName: '', financeBook: 'freelance' });
      setSelectedMembers([]);
      fetchData();
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error('Failed to create project');
    }
  };

  const handleDeleteClick = (id, e) => {
    e.stopPropagation();
    setProjectToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!projectToDelete) return;

    try {
      await axios.delete(`${API}/projects/${projectToDelete}`);
      toast.success('✅ Successfully deleted');
      setDeleteDialogOpen(false);
      setProjectToDelete(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('⚠️ Deletion failed. Please try again later');
      setDeleteDialogOpen(false);
      setProjectToDelete(null);
    }
  };

  // Dashboard title editing handlers
  const startEditingTitle = () => {
    setTempTitle(dashboardTitle);
    setIsEditingTitle(true);
  };

  const saveDashboardTitle = () => {
    if (tempTitle.trim()) {
      setDashboardTitle(tempTitle.trim());
      setIsEditingTitle(false);
      toast.success('Dashboard title updated');
    }
  };

  const cancelEditingTitle = () => {
    setIsEditingTitle(false);
    setTempTitle('');
  };

  // Project name editing handlers
  const startEditingProject = (e, projectId, currentName) => {
    e.stopPropagation();
    setEditingProjectId(projectId);
    setEditingProjectName(currentName);
  };

  const saveProjectName = async (e, projectId) => {
    e.stopPropagation();
    
    if (!editingProjectName.trim()) {
      toast.error('Project name cannot be empty');
      return;
    }

    try {
      await axios.put(`${API}/projects/${projectId}`, {
        name: editingProjectName.trim()
      });
      toast.success('Project name updated');
      setEditingProjectId(null);
      setEditingProjectName('');
      fetchData();
    } catch (error) {
      console.error('Error updating project name:', error);
      toast.error('Failed to update project name');
    }
  };

  const cancelEditingProject = (e) => {
    e.stopPropagation();
    setEditingProjectId(null);
    setEditingProjectName('');
  };

  const getMilestoneColor = (milestone) => {
    const colors = {
      'Schematic': 'bg-blue-100 text-blue-800',
      'DED': 'bg-yellow-100 text-yellow-800',
      'For Construction': 'bg-orange-100 text-orange-800',
      'Done': 'bg-green-100 text-green-800'
    };
    return colors[milestone] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/hub')}
                  className="text-slate-500 hover:text-slate-700 -ml-2"
                  data-testid="back-to-hub-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Hub
                </Button>
              </div>
              <span className="text-[10px] font-mono text-slate-400 bg-slate-100 px-2 py-0.5 rounded absolute top-4 right-6">A</span>
              {isEditingTitle ? (
                <div className="flex items-center gap-2 mb-2">
                  <Input
                    value={tempTitle}
                    onChange={(e) => setTempTitle(e.target.value)}
                    className="text-4xl font-bold text-slate-800 h-14 max-w-md"
                    style={{fontFamily: 'Work Sans, sans-serif'}}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveDashboardTitle();
                      if (e.key === 'Escape') cancelEditingTitle();
                    }}
                  />
                  <Button
                    size="sm"
                    onClick={saveDashboardTitle}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Check className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={cancelEditingTitle}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2 mb-2 group">
                  <h1 className="text-4xl font-bold text-slate-800" style={{fontFamily: 'Work Sans, sans-serif'}}>
                    {dashboardTitle}
                  </h1>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={startEditingTitle}
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Pencil className="w-4 h-4 text-slate-600" />
                  </Button>
                </div>
              )}
              <p className="text-slate-600">Cremona Structure Engineering</p>
            </div>
            <div className="flex gap-3">
              {!seeded && projects.length === 0 && (
                <Button onClick={seedData} variant="outline" data-testid="seed-data-btn">
                  Load Sample Data
                </Button>
              )}
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-project-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    New Project
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Create New Project</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Label htmlFor="name">Project Name</Label>
                      <Input
                        id="name"
                        data-testid="project-name-input"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        placeholder="Enter project name"
                      />
                    </div>

                    {/* After Service */}
                    <div className="flex items-center gap-3 p-3 bg-amber-50 rounded border border-amber-200">
                      <input
                        type="checkbox"
                        checked={formData.isAfterService}
                        onChange={(e) => setFormData({...formData, isAfterService: e.target.checked, contractValue: e.target.checked ? '0' : formData.contractValue})}
                        data-testid="after-service-checkbox"
                        className="rounded"
                      />
                      <div>
                        <Label className="cursor-pointer">After Service Work</Label>
                        <p className="text-xs text-amber-700">No client fee — maintenance/fixes for a completed project</p>
                      </div>
                    </div>

                    {formData.isAfterService && (
                      <div>
                        <Label>Related Project (Optional)</Label>
                        <Select
                          value={formData.relatedProjectId}
                          onValueChange={(val) => {
                            const p = projects.find(pr => pr.id === val);
                            setFormData({...formData, relatedProjectId: val, relatedProjectName: p?.name || ''});
                          }}
                        >
                          <SelectTrigger data-testid="related-project-select">
                            <SelectValue placeholder="Link to original project" />
                          </SelectTrigger>
                          <SelectContent>
                            {projects.filter(p => !p.isAfterService).map(p => (
                              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {/* Finance Book */}
                    <div>
                      <Label>Finance Book</Label>
                      <Select value={formData.financeBook} onValueChange={(val) => setFormData({...formData, financeBook: val})}>
                        <SelectTrigger data-testid="finance-book-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="freelance">Freelance (BCA - Johan)</SelectItem>
                          <SelectItem value="company">Company (BCA - Cremona)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Client Selection */}
                    <div>
                      <Label>Client</Label>
                      <Select 
                        value={formData.clientId} 
                        onValueChange={(val) => {
                          const client = clients.find(c => c.id === val);
                          setFormData({...formData, clientId: val, clientName: client ? `${client.name} - ${client.company}` : ''});
                        }}
                      >
                        <SelectTrigger data-testid="select-client">
                          <SelectValue placeholder="Select client (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          {clients.map(c => (
                            <SelectItem key={c.id} value={c.id}>{c.name} - {c.company}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Sub-Works */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label>Sub-Works / Bangunan</Label>
                        <Button type="button" variant="outline" size="sm" onClick={addSubWork}>
                          <Plus className="w-3 h-3 mr-1" /> Add Sub-Work
                        </Button>
                      </div>
                      {formData.subWorks.length > 0 && (
                        <div className="space-y-2">
                          {formData.subWorks.map((sw, i) => (
                            <div key={sw.id} className="flex gap-2 items-center">
                              <Input
                                value={sw.name}
                                onChange={(e) => updateSubWork(i, 'name', e.target.value)}
                                placeholder="Sub-work name"
                                className="flex-1"
                              />
                              <Input
                                type="number"
                                value={sw.price || ''}
                                onChange={(e) => updateSubWork(i, 'price', e.target.value)}
                                placeholder="Price (Rp)"
                                className="w-40"
                              />
                              <Button type="button" variant="ghost" size="sm" onClick={() => removeSubWork(i)}>
                                <Trash2 className="w-3 h-3 text-red-500" />
                              </Button>
                            </div>
                          ))}
                          <p className="text-xs text-slate-500 text-right">
                            Sub-Works Total: {formatCurrency(formData.subWorks.reduce((s, w) => s + (w.price || 0), 0))}
                          </p>
                        </div>
                      )}
                    </div>

                    {!formData.isAfterService && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="contractValue">Contract Value (Rp) {formData.subWorks.length > 0 && <span className="text-xs text-slate-400">(or leave for sub-works total)</span>}</Label>
                        <Input
                          id="contractValue"
                          type="number"
                          data-testid="contract-value-input"
                          value={formData.contractValue}
                          onChange={(e) => setFormData({...formData, contractValue: e.target.value})}
                          placeholder={formData.subWorks.length > 0 ? 'Auto from sub-works' : 'Enter contract value'}
                        />
                      </div>
                      <div>
                        <Label htmlFor="initialPaid">Initial Paid (Rp)</Label>
                        <Input
                          id="initialPaid"
                          type="number"
                          data-testid="initial-paid-input"
                          value={formData.initialPaid || ''}
                          onChange={(e) => setFormData({...formData, initialPaid: e.target.value})}
                          placeholder="0"
                        />
                        <p className="text-xs text-slate-600 italic mt-1">(Paid before project integrated to program)</p>
                      </div>
                    </div>
                    )}
                    
                    <div>
                      <Label>Upload Project Contract (PDF/JPG)</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <label className="flex items-center gap-2 px-4 py-2 text-sm bg-slate-100 hover:bg-slate-200 rounded cursor-pointer transition-colors">
                          <Upload className="w-4 h-4" />
                          {formData.contractFileName || 'Choose file'}
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            className="hidden"
                            data-testid="project-contract-upload"
                            onChange={handleProjectContractUpload}
                          />
                        </label>
                      </div>
                    </div>

                    <div>
                      <Label>Team Members ({selectedMembers.length} added)</Label>
                      <p className="text-xs text-slate-600 mb-2">Select members one by one to add to this project</p>
                      <div className="flex gap-2 mb-3">
                        <Select onValueChange={addMember} value="">
                          <SelectTrigger data-testid="select-team-member" className="w-full">
                            <SelectValue placeholder="+ Click to add team member" />
                          </SelectTrigger>
                          <SelectContent>
                            {teamMembers
                              .filter(tm => !selectedMembers.find(sm => sm.name === tm.name))
                              .map(member => (
                                <SelectItem key={member.id} value={member.name}>
                                  {member.name} - {member.role}
                                </SelectItem>
                              ))}
                            {teamMembers.filter(tm => !selectedMembers.find(sm => sm.name === tm.name)).length === 0 && (
                              <div className="p-2 text-sm text-slate-500 text-center">
                                All team members added
                              </div>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {selectedMembers.length > 0 && (
                        <div className="space-y-3 mt-4">
                          {selectedMembers.map((member, index) => (
                            <Card key={index}>
                              <CardContent className="p-4">
                                <div className="flex items-center justify-between mb-3">
                                  <div>
                                    <div className="font-medium">{member.name}</div>
                                    <div className="text-sm text-slate-600">{member.role}</div>
                                  </div>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => removeMember(index)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <Label className="text-xs">Contract Value (Rp)</Label>
                                    <Input
                                      type="number"
                                      data-testid={`member-contract-${index}`}
                                      value={member.contractValue}
                                      onChange={(e) => updateMemberValue(index, 'contractValue', e.target.value)}
                                      placeholder="0"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs">Initial Paid (Rp)</Label>
                                    <Input
                                      type="number"
                                      data-testid={`member-initial-paid-${index}`}
                                      value={member.initialPaid || 0}
                                      onChange={(e) => updateMemberValue(index, 'initialPaid', e.target.value)}
                                      placeholder="0"
                                    />
                                    <p className="text-xs text-slate-500 italic mt-1">(Before integration)</p>
                                  </div>
                                </div>
                                <div className="mt-3">
                                  <Label className="text-xs">Tanggal Penugasan (Assignment Date)</Label>
                                  <Input
                                    type="date"
                                    data-testid={`member-assignment-date-${index}`}
                                    value={member.assignmentDate || ''}
                                    onChange={(e) => updateMemberValue(index, 'assignmentDate', e.target.value)}
                                  />
                                </div>
                                <div className="mt-3">
                                  <Label className="text-xs">Work Description / Contract Summary</Label>
                                  <Textarea
                                    data-testid={`member-contract-desc-${index}`}
                                    value={member.contractDescription || ''}
                                    onChange={(e) => updateMemberValue(index, 'contractDescription', e.target.value)}
                                    placeholder="Describe the work scope for this team member..."
                                    className="min-h-[60px] text-sm"
                                  />
                                </div>
                                <div className="mt-3">
                                  <Label className="text-xs">Upload Contract (PDF/JPG)</Label>
                                  <div className="flex items-center gap-2 mt-1">
                                    <label className="flex items-center gap-2 px-3 py-1.5 text-xs bg-slate-100 hover:bg-slate-200 rounded cursor-pointer transition-colors">
                                      <Upload className="w-3 h-3" />
                                      {member.contractFileName || 'Choose file'}
                                      <input
                                        type="file"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        className="hidden"
                                        onChange={(e) => handleMemberFileUpload(index, e)}
                                      />
                                    </label>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex justify-end gap-2 pt-4">
                      <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" data-testid="submit-project-btn">Create Project</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <Card className="bg-white shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Active Projects</p>
                    <p className="text-2xl font-bold text-slate-800">{allProjects.filter(p => !p.archived).length}</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <FolderOpen className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/team')}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Team Members</p>
                    <p className="text-2xl font-bold text-slate-800">{teamMembers.length}</p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <Users className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white shadow-sm hover:shadow-md transition-shadow cursor-pointer relative" onClick={() => navigate('/finance?book=freelance')}>
              <button
                type="button"
                data-testid="freelance-breakdown-btn"
                onClick={(e) => { e.stopPropagation(); setBreakdownBook('freelance'); }}
                className="absolute top-2 right-2 p-1 rounded-full hover:bg-purple-100 text-purple-600"
                title="Contract vs Income Breakdown"
              >
                <Info className="w-4 h-4" />
              </button>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Freelance Book</p>
                    <p className="text-xl font-bold text-slate-800">
                      {formatCurrency(allProjects.filter(p => (p.financeBook || 'freelance') === 'freelance').reduce((sum, p) => sum + p.contractValue, 0))}
                    </p>
                  </div>
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white shadow-sm hover:shadow-md transition-shadow cursor-pointer relative" onClick={() => navigate('/finance?book=company')}>
              <button
                type="button"
                data-testid="company-breakdown-btn"
                onClick={(e) => { e.stopPropagation(); setBreakdownBook('company'); }}
                className="absolute top-2 right-2 p-1 rounded-full hover:bg-indigo-100 text-indigo-600"
                title="Contract vs Income Breakdown"
              >
                <Info className="w-4 h-4" />
              </button>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Company Book</p>
                    <p className="text-xl font-bold text-slate-800">
                      {formatCurrency(allProjects.filter(p => p.financeBook === 'company').reduce((sum, p) => sum + p.contractValue, 0))}
                    </p>
                  </div>
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-indigo-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/clients')}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Clients</p>
                    <p className="text-2xl font-bold text-slate-800">{clients.length}</p>
                  </div>
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                    <UserCircle className="w-6 h-6 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        <Card className="bg-white shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle>All Projects{showArchived ? ' — Archived' : ''}</CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const next = !showArchived;
                setShowArchived(next);
                fetchData({ archived: next });
              }}
              data-testid="toggle-archived-btn"
            >
              {showArchived ? <><EyeOff className="w-4 h-4 mr-2" /> Hide Archived</> : <><Eye className="w-4 h-4 mr-2" /> Show Archived</>}
            </Button>
          </CardHeader>
          <CardContent>
            {projects.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-slate-600 mb-4">No projects yet. Create your first project or load sample data!</p>
              </div>
            ) : (
              <>
                {hiddenCols.length > 0 && (
                  <div className="mb-2 flex items-center gap-2">
                    <span className="text-xs text-slate-500">{hiddenCols.length} column(s) hidden</span>
                    <Button size="sm" variant="ghost" onClick={showAllCols} data-testid="show-all-cols-btn" className="h-6 px-2 text-xs">
                      <Eye className="w-3 h-3 mr-1" /> Show all
                    </Button>
                  </div>
                )}
                <div className="overflow-x-auto">
                <Table style={{ tableLayout: 'fixed', width: 'max-content', minWidth: '100%' }}>
                <TableHeader>
                  <TableRow>
                    {COLUMNS.filter(c => showCol(c.key)).map(c => (
                      <TableHead
                        key={c.key}
                        style={{ width: colWidths[c.key], minWidth: colWidths[c.key] }}
                        className={`relative select-none ${c.key === 'actions' ? 'text-right' : ''}`}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          setCtxMenu({ x: e.clientX, y: e.clientY, colKey: c.key });
                        }}
                        data-testid={`col-head-${c.key}`}
                      >
                        {c.label}
                        <div
                          onMouseDown={(e) => startResize(e, c.key)}
                          onClick={(e) => e.stopPropagation()}
                          onContextMenu={(e) => e.stopPropagation()}
                          className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-blue-400/60 active:bg-blue-500 z-10"
                          style={{ touchAction: 'none' }}
                          data-testid={`col-resize-${c.key}`}
                        />
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {projects.map(project => (
                    <TableRow
                      key={project.id}
                      className={`cursor-pointer hover:bg-slate-50 ${dragOverRowId === project.id ? 'bg-blue-50' : ''} ${dragRowId === project.id ? 'opacity-50' : ''}`}
                      onClick={() => navigate(`/project/${project.id}`)}
                      draggable
                      onDragStart={(e) => onRowDragStart(e, project.id)}
                      onDragOver={(e) => onRowDragOver(e, project.id)}
                      onDrop={(e) => onRowDrop(e, project.id)}
                      onDragEnd={() => { setDragRowId(null); setDragOverRowId(null); }}
                      data-testid={`project-row-${project.id}`}
                    >
                      {showCol('name') && (
                      <TableCell className="font-medium" style={{ width: colWidths.name }}>
                        {editingProjectId === project.id ? (
                          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <Input
                              value={editingProjectName}
                              onChange={(e) => setEditingProjectName(e.target.value)}
                              className="max-w-xs"
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveProjectName(e, project.id);
                                if (e.key === 'Escape') cancelEditingProject(e);
                              }}
                            />
                            <Button size="sm" onClick={(e) => saveProjectName(e, project.id)} className="bg-green-600 hover:bg-green-700">
                              <Check className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={cancelEditingProject}>
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 group">
                            <span className="cursor-grab text-slate-400 hover:text-slate-600" title="Drag to reorder" onClick={(e) => e.stopPropagation()}>⋮⋮</span>
                            <span className="truncate">{project.name}</span>
                            {project.isAfterService && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded font-medium">After Service</span>
                            )}
                            <Button size="sm" variant="ghost" onClick={(e) => startEditingProject(e, project.id, project.name)} className="opacity-0 group-hover:opacity-100 transition-opacity p-1 h-6">
                              <Pencil className="w-3 h-3 text-slate-600" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                      )}
                      {showCol('client') && (<TableCell className="text-sm text-slate-600 truncate" style={{ width: colWidths.client }}>{project.clientName || '-'}</TableCell>)}
                      {showCol('book') && (
                      <TableCell style={{ width: colWidths.book }}>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${(project.financeBook || 'freelance') === 'company' ? 'bg-indigo-100 text-indigo-800' : 'bg-purple-100 text-purple-800'}`}>
                          {(project.financeBook || 'freelance') === 'company' ? 'Company' : 'Freelance'}
                        </span>
                      </TableCell>
                      )}
                      {showCol('contract') && (<TableCell style={{ width: colWidths.contract }}>{formatCurrency(project.contractValue)}</TableCell>)}
                      {showCol('paid') && (<TableCell style={{ width: colWidths.paid }}>{formatCurrency((project.initialPaid || 0) + (project.amountPaid || 0))}</TableCell>)}
                      {showCol('teamContract') && (
                      <TableCell style={{ width: colWidths.teamContract }}>
                        {formatCurrency(project.teamMembers?.reduce((sum, member) => sum + (member.contractValue || 0), 0) || 0)}
                      </TableCell>
                      )}
                      {showCol('teamPaid') && (
                      <TableCell style={{ width: colWidths.teamPaid }}>
                        {formatCurrency(project.teamMembers?.reduce((sum, member) => sum + ((member.initialPaid || 0) + (member.amountPaid || 0)), 0) || 0)}
                      </TableCell>
                      )}
                      {showCol('net') && (
                      <TableCell style={{ width: colWidths.net }}>
                        {(() => {
                          const totalPaidFromClient = (project.initialPaid || 0) + (project.amountPaid || 0);
                          const paidToTeam = project.teamMembers?.reduce((sum, member) => sum + ((member.initialPaid || 0) + (member.amountPaid || 0)), 0) || 0;
                          const otherExpenses = project.otherExpenses || 0;
                          const netProfit = totalPaidFromClient - paidToTeam - otherExpenses;
                          return (
                            <span className={`font-semibold ${netProfit >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                              {formatCurrency(netProfit)}
                            </span>
                          );
                        })()}
                      </TableCell>
                      )}
                      {showCol('projNet') && (
                      <TableCell style={{ width: colWidths.projNet }}>
                        {(() => {
                          const totalTeamContract = project.teamMembers?.reduce((sum, member) => sum + (member.contractValue || 0), 0) || 0;
                          const otherExpenses = project.otherExpenses || 0;
                          const projectedNetProfit = project.contractValue - totalTeamContract - otherExpenses;
                          return (
                            <span className={`font-semibold ${projectedNetProfit >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                              {formatCurrency(projectedNetProfit)}
                            </span>
                          );
                        })()}
                      </TableCell>
                      )}
                      {showCol('milestone') && (
                      <TableCell style={{ width: colWidths.milestone }}>
                        <Badge className={getMilestoneColor(project.milestone)}>{project.milestone}</Badge>
                      </TableCell>
                      )}
                      {showCol('pending') && (
                      <TableCell style={{ width: colWidths.pending }}>
                        <Badge variant="outline">{project.pendingTasks || 0}</Badge>
                      </TableCell>
                      )}
                      {showCol('teamSize') && (<TableCell style={{ width: colWidths.teamSize }}>{project.teamMembers?.length || 0}</TableCell>)}
                      {showCol('actions') && (
                      <TableCell className="text-right" style={{ width: colWidths.actions }}>
                        <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                          {project.archived ? (
                            <Button variant="ghost" size="sm" onClick={(e) => toggleArchive(e, project)} title="Restore project" data-testid={`unarchive-project-${project.id}`}>
                              <ArchiveRestore className="w-4 h-4 text-emerald-600" />
                            </Button>
                          ) : (
                            <Button variant="ghost" size="sm" disabled={!canArchive(project)} onClick={(e) => toggleArchive(e, project)} title={canArchive(project) ? 'Archive this finished project' : 'Cannot archive yet — outstanding tasks, unpaid client, unpaid team, or pending postponed planner.'} data-testid={`archive-project-${project.id}`}>
                              <Archive className={`w-4 h-4 ${canArchive(project) ? 'text-slate-700' : 'text-slate-300'}`} />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={(e) => handleDeleteClick(project.id, e)} data-testid={`delete-project-${project.id}`}>
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        </div>
                      </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          onConfirm={confirmDelete}
          title="Delete Project"
          description="Are you sure you want to delete this project? This will also delete all related tasks and transactions. This action cannot be undone."
        />

        {/* Column right-click hide menu */}
        {ctxMenu && (
          <div
            style={{ position: 'fixed', top: ctxMenu.y, left: ctxMenu.x, zIndex: 100 }}
            className="bg-white border shadow-lg rounded py-1 text-sm"
            onClick={(e) => e.stopPropagation()}
            data-testid="col-context-menu"
          >
            <button
              className="w-full px-3 py-1.5 text-left hover:bg-slate-100 flex items-center gap-2"
              onClick={() => { hideCol(ctxMenu.colKey); setCtxMenu(null); }}
              data-testid="hide-col-btn"
            >
              <EyeOff className="w-3.5 h-3.5" /> Hide column
            </button>
            {hiddenCols.length > 0 && (
              <button
                className="w-full px-3 py-1.5 text-left hover:bg-slate-100 flex items-center gap-2"
                onClick={() => { showAllCols(); setCtxMenu(null); }}
              >
                <Eye className="w-3.5 h-3.5" /> Show all columns
              </button>
            )}
          </div>
        )}

        {/* Book Breakdown Dialog (Contract vs Received) */}
        <Dialog open={!!breakdownBook} onOpenChange={(o) => !o && setBreakdownBook(null)}>
          <DialogContent className="max-w-lg" data-testid="book-breakdown-dialog">
            {breakdownBook && (() => {
              const bookProjects = allProjects.filter(p => (p.financeBook || 'freelance') === breakdownBook);
              const totalContract = bookProjects.reduce((s, p) => s + (p.contractValue || 0), 0);
              const totalIncome = bookIncome[breakdownBook] || 0;
              const difference = totalContract - totalIncome;
              const outstandingProjects = bookProjects
                .map(p => ({
                  name: p.name,
                  outstanding: (p.contractValue || 0) - (p.initialPaid || 0) - (p.amountPaid || 0)
                }))
                .filter(p => p.outstanding > 0)
                .sort((a, b) => b.outstanding - a.outstanding);
              const label = breakdownBook === 'freelance' ? 'Freelance Book' : 'Company Book';
              const accent = breakdownBook === 'freelance' ? 'text-purple-600' : 'text-indigo-600';
              return (
                <>
                  <DialogHeader>
                    <DialogTitle>{label} — Breakdown</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3 py-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">{label} (Total Contract)</span>
                      <span className={`font-semibold ${accent}`}>{formatCurrency(totalContract)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Total Income So Far</span>
                      <span className="font-semibold text-green-600">{formatCurrency(totalIncome)}</span>
                    </div>
                    <div className="border-t pt-3 flex justify-between text-sm">
                      <span className="text-slate-800 font-semibold">The Difference</span>
                      <span className="font-bold text-amber-600" data-testid="breakdown-difference">{formatCurrency(difference)}</span>
                    </div>
                    <div className="pt-2">
                      <p className="text-sm text-slate-700 font-medium mb-2">Consist Of:</p>
                      {outstandingProjects.length === 0 ? (
                        <p className="text-sm text-slate-500 italic">No outstanding projects.</p>
                      ) : (
                        <div className="space-y-1 max-h-72 overflow-y-auto">
                          {outstandingProjects.map((p, i) => (
                            <div key={i} className="flex justify-between text-sm py-1 border-b border-slate-100">
                              <span className="text-slate-700 truncate pr-2">{p.name}</span>
                              <span className="text-slate-800 font-medium whitespace-nowrap">Outstanding: {formatCurrency(p.outstanding)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              );
            })()}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Dashboard;